import {AgeniumUser, AgentData, InternalUser, InternalUserRole} from '@amplify/athena-models';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Grid,
    TextField,
    Typography,
} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';
import RootStore from '../Core/mobx/RootStore';
import {allRoles} from './constants';

export const CreateAgentPanel = observer(({open, close}: {open: boolean; close: (changed?: boolean) => void}) => {
    const userAdminStore = RootStore.userAdminStore;
    const uiStore = RootStore.uiStore;

    const [isFormValid, setIsFormValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [user, setUser] = useState<InternalUser>({
        email: '',
        roles: [],
        ageniumUserData: undefined,
    });

    useEffect(() => {
        let valid = true;

        if (!user.email) {
            valid = false;
        }

        if (user.roles?.includes(InternalUserRole.AGENIUM_AGENT)) {
            const agenium = user.ageniumUserData || getDefaultAgeniumUser();
            if (
                !agenium.keyedContractNumber ||
                !agenium.agentAssistedContractNumber ||
                !agenium.agentKeyedFirstName ||
                !agenium.agentKeyedLastName
            ) {
                valid = false;
            }
        }

        setIsFormValid(valid);
    }, [user]);

    function getNewDefaultUser(): InternalUser {
        return {
            email: '',
            roles: [InternalUserRole.AGENT],
            ageniumUserData: undefined,
        };
    }

    function getDefaultAgentUserData(): AgentData {
        return {
            hubspotId: '',
            managerName: '',
            managerLicenseNumber: '',
        };
    }
    function getDefaultAgeniumUser(): AgeniumUser {
        return {
            keyedContractNumber: '',
            agentAssisted: true,
            agentAssistedContractNumber: '',
            // TODO get from google? are these required?
            agentKeyedFirstName: '',
            agentKeyedLastName: '',
        };
    }

    function handleCheck(role: InternalUserRole, isChecked: boolean) {
        const updatedRoles = isChecked
            ? [...(user.roles || []), role]
            : user.roles?.filter((existingRole) => existingRole !== role);

        const updatedUser = {...user, roles: updatedRoles};

        if (isChecked && role === InternalUserRole.AGENIUM_AGENT) {
            updatedUser.ageniumUserData = getDefaultAgeniumUser();
        } else if (!isChecked && role === InternalUserRole.AGENIUM_AGENT) {
            updatedUser.ageniumUserData = null;
        }

        setUser(updatedUser);
    }

    async function createInternalUsers() {
        try {
            setIsLoading(true);
            if (!user.email) {
                throw new Error('User must have an email.');
            }

            if (user.roles?.includes(InternalUserRole.AGENIUM_AGENT)) {
                const agenium = user.ageniumUserData || getDefaultAgeniumUser();
                if (
                    !agenium.keyedContractNumber ||
                    !agenium.agentAssistedContractNumber ||
                    !agenium.agentKeyedFirstName ||
                    !agenium.agentKeyedLastName
                ) {
                    throw new Error(`Agenium Agent fields must be filled for user: ${user.email || 'unknown email'}`);
                }
            }

            await userAdminStore.addInternalUsers([user]);
            uiStore.showSnackbar('User successfully created.', 'success');
            setUser(getNewDefaultUser());
            close(true);
        } catch (error: any) {
            RootStore.uiStore.showSnackbar(error.message || 'Failed to create user.', 'error');
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Dialog fullWidth maxWidth="lg" open={open} onClose={() => close(false)} aria-labelledby="form-dialog-title">
            <DialogTitle id="form-dialog-title">Create Internal User</DialogTitle>
            <DialogContent>
                <DialogContentText>Enter email and roles of new Internal User</DialogContentText>
                <Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Email"
                                value={user.email}
                                variant="outlined"
                                onChange={(e) => setUser({...user, email: e.target.value})}
                                fullWidth
                            />
                        </Grid>
                    </Grid>

                    <Box style={{display: 'flex', marginTop: '10px'}}>
                        {allRoles.map((role) => (
                            <FormControlLabel
                                key={role.role}
                                control={
                                    <Checkbox
                                        checked={user.roles?.includes(role.role)}
                                        onChange={(e) => handleCheck(role.role, e.target.checked)}
                                    />
                                }
                                label={role.displayName}
                            />
                        ))}
                    </Box>
                    {user.roles?.includes(InternalUserRole.AGENT) && (
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <TextField
                                    variant="outlined"
                                    label="Manager Name"
                                    value={user.agentData?.managerName || ''}
                                    onChange={(e) =>
                                        setUser({
                                            ...user,
                                            agentData: {
                                                ...(user.agentData || getDefaultAgentUserData()),
                                                managerName: e.target.value,
                                            },
                                        })
                                    }
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    variant="outlined"
                                    label="Manager License Number"
                                    value={user.agentData?.managerLicenseNumber || ''}
                                    onChange={(e) =>
                                        setUser({
                                            ...user,
                                            agentData: {
                                                ...(user.agentData || getDefaultAgentUserData()),
                                                managerLicenseNumber: e.target.value,
                                            },
                                        })
                                    }
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    variant="outlined"
                                    label="Hubspot ID"
                                    value={user.agentData?.hubspotId || ''}
                                    onChange={(e) =>
                                        setUser({
                                            ...user,
                                            agentData: {
                                                ...(user.agentData || getDefaultAgentUserData()),
                                                hubspotId: e.target.value,
                                            },
                                        })
                                    }
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    )}

                    {user.roles?.includes(InternalUserRole.AGENIUM_AGENT) && (
                        <Grid container spacing={2} style={{marginTop: '10px'}}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Keyed Contract Number"
                                    value={user.ageniumUserData?.keyedContractNumber || ''}
                                    variant="outlined"
                                    onChange={(e) =>
                                        setUser({
                                            ...user,
                                            ageniumUserData: {
                                                ...(user.ageniumUserData || getDefaultAgeniumUser()),
                                                keyedContractNumber: e.target.value,
                                            },
                                        })
                                    }
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Agent Assisted Contract Number"
                                    value={user.ageniumUserData?.agentAssistedContractNumber || ''}
                                    variant="outlined"
                                    onChange={(e) =>
                                        setUser({
                                            ...user,
                                            ageniumUserData: {
                                                ...(user.ageniumUserData || getDefaultAgeniumUser()),
                                                agentAssistedContractNumber: e.target.value,
                                            },
                                        })
                                    }
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Agent Keyed First Name"
                                    variant="outlined"
                                    value={user.ageniumUserData?.agentKeyedFirstName || ''}
                                    onChange={(e) =>
                                        setUser({
                                            ...user,
                                            ageniumUserData: {
                                                ...(user.ageniumUserData || getDefaultAgeniumUser()),
                                                agentKeyedFirstName: e.target.value,
                                            },
                                        })
                                    }
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Agent Keyed Last Name"
                                    variant="outlined"
                                    value={user.ageniumUserData?.agentKeyedLastName || ''}
                                    onChange={(e) =>
                                        setUser({
                                            ...user,
                                            ageniumUserData: {
                                                ...(user.ageniumUserData || getDefaultAgeniumUser()),
                                                agentKeyedLastName: e.target.value,
                                            },
                                        })
                                    }
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Typography>Is Agent Assisted</Typography>
                                <Checkbox
                                    checked={user.ageniumUserData?.agentAssisted ?? true}
                                    onChange={(e) =>
                                        setUser({
                                            ...user,
                                            ageniumUserData: {
                                                ...(user.ageniumUserData || getDefaultAgeniumUser()),
                                                agentAssisted: e.target.checked,
                                            },
                                        })
                                    }
                                />
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => close(false)} color="primary">
                    Cancel
                </Button>
                <Button
                    onClick={createInternalUsers}
                    color="primary"
                    startIcon={isLoading ? <CircularProgress size={20} /> : null}
                    disabled={!isFormValid || isLoading}
                >
                    {isLoading ? 'Creating...' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
});
